import { useState } from 'react';
import { format } from 'date-fns';
import { Plus, Edit, Copy, Archive, Eye, EyeOff, Users, Building, BarChart3, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAdminWelcomeMessages } from '@/hooks/useAdminWelcomeMessages';
import { WelcomeMessageDialog } from '@/components/admin/WelcomeMessageDialog';
import { WelcomeMessageAnalyticsDialog } from '@/components/admin/WelcomeMessageAnalyticsDialog';
import type { WelcomeMessage } from '@/hooks/useWelcomeMessages';
import type { WelcomeMessageWithAnalytics, CreateWelcomeMessageInput } from '@/hooks/useAdminWelcomeMessages';

function getStatusBadge(message: WelcomeMessageWithAnalytics) {
  const now = new Date();
  const startDate = new Date(message.start_date);
  const endDate = message.end_date ? new Date(message.end_date) : null;

  if (message.status === 'archived') {
    return <Badge variant="secondary">Archived</Badge>;
  }

  if (message.status === 'draft') {
    return <Badge variant="outline">Draft</Badge>;
  }

  if (startDate > now) {
    return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Scheduled</Badge>;
  }

  if (endDate && endDate < now) {
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Expired</Badge>;
  }

  return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
}

function getAudienceBadge(audience: string) {
  switch (audience) {
    case 'vendors':
      return <Badge variant="outline" className="gap-1"><Users className="h-3 w-3" />Vendors</Badge>;
    case 'clients':
      return <Badge variant="outline" className="gap-1"><Building className="h-3 w-3" />Clients</Badge>;
    default:
      return <Badge variant="outline">All Users</Badge>;
  }
}

function getStyleIndicator(style: string) {
  const colors: Record<string, string> = {
    info: 'bg-blue-500',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    important: 'bg-red-500',
  };
  return (
    <div className="flex items-center gap-2">
      <div className={cn('w-3 h-3 rounded-full', colors[style])} />
      <span className="capitalize text-sm">{style}</span>
    </div>
  );
}

export default function AdminWelcomeMessagesPage() {
  const {
    messages,
    isLoading,
    createMessage,
    updateMessage,
    archiveMessage,
    duplicateMessage,
    isCreating,
    isUpdating,
  } = useAdminWelcomeMessages();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<WelcomeMessage | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [messageToArchive, setMessageToArchive] = useState<WelcomeMessage | null>(null);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [analyticsMessage, setAnalyticsMessage] = useState<WelcomeMessageWithAnalytics | null>(null);

  const handleOpenAnalytics = (message: WelcomeMessageWithAnalytics) => {
    setAnalyticsMessage(message);
    setAnalyticsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingMessage(null);
    setDialogOpen(true);
  };

  const handleEdit = (message: WelcomeMessage) => {
    setEditingMessage(message);
    setDialogOpen(true);
  };

  const handleSave = async (data: CreateWelcomeMessageInput) => {
    if (editingMessage) {
      await updateMessage({ id: editingMessage.id, ...data });
    } else {
      await createMessage(data);
    }
  };

  const handleArchiveClick = (message: WelcomeMessage) => {
    setMessageToArchive(message);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (messageToArchive) {
      await archiveMessage(messageToArchive.id);
    }
    setArchiveDialogOpen(false);
    setMessageToArchive(null);
  };

  const handleDuplicate = async (message: WelcomeMessage) => {
    await duplicateMessage(message);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Welcome Messages</h1>
          <p className="text-muted-foreground">
            Configure modal messages shown to users when they log in
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Message
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Messages</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {messages.filter(m => m.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {messages.reduce((acc, m) => acc + m.views_count, 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Dismissal Rate</CardTitle>
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {messages.length > 0
                ? Math.round(
                    (messages
                      .filter(m => m.display_mode === 'dismissable' && m.views_count > 0)
                      .reduce((acc, m) => acc + (m.dismissals_count / m.views_count) * 100, 0) /
                      Math.max(1, messages.filter(m => m.display_mode === 'dismissable' && m.views_count > 0).length))
                  )
                : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Messages</CardTitle>
          <CardDescription>
            Manage welcome messages displayed to users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No welcome messages yet</p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first message
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Style</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Analytics</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {message.title}
                    </TableCell>
                    <TableCell>{getStatusBadge(message)}</TableCell>
                    <TableCell>{getAudienceBadge(message.target_audience)}</TableCell>
                    <TableCell>{getStyleIndicator(message.style)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(message.start_date), 'MMM d, yyyy')}
                      {message.end_date && (
                        <> → {format(new Date(message.end_date), 'MMM d, yyyy')}</>
                      )}
                    </TableCell>
                    <TableCell>{message.priority}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-sm gap-1 h-auto py-1 px-2"
                        onClick={() => handleOpenAnalytics(message)}
                      >
                        <BarChart3 className="h-3.5 w-3.5" />
                        {message.views_count} views
                        {message.display_mode === 'dismissable' && (
                          <> · {message.dismissals_count} dismissed</>
                        )}
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            •••
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(message)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(message)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {message.status !== 'archived' && (
                            <DropdownMenuItem
                              onClick={() => handleArchiveClick(message)}
                              className="text-destructive"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <WelcomeMessageDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        message={editingMessage}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
      />

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Welcome Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive "{messageToArchive?.title}"? 
              It will no longer be shown to users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Analytics Dialog */}
      <WelcomeMessageAnalyticsDialog
        open={analyticsDialogOpen}
        onOpenChange={setAnalyticsDialogOpen}
        message={analyticsMessage}
      />
    </div>
  );
}
